import type { ReactNode, SelectHTMLAttributes } from 'react'

import { forwardRef } from 'react'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
    label?: ReactNode
    error?: string
    children: ReactNode
}

const selectBase = 'block w-full appearance-none rounded-card border border-transparent bg-white/80 px-4 py-3 pr-11 text-base text-neutral-900 shadow-[inset_0_0_0_1px_rgba(0,0,0,0.04)] focus:border-brand-red/50 focus:bg-white focus:outline-none focus:ring-4 focus:ring-brand-red/15 transition-colors'

const Select = forwardRef<HTMLSelectElement, SelectProps>(({ className, label, error, id, children, ...props }, ref) => {
    const selectId = id ?? props.name

    return (
        <div className='flex flex-col gap-1.5'>
            {label ? (
                <label htmlFor={selectId} className='text-sm font-medium text-neutral-700'>
                    {label}
                </label>
            ) : null}

            <div className='relative'>
                <select
                    ref={ref}
                    id={selectId}
                    aria-invalid={Boolean(error) || undefined}
                    aria-describedby={error ? `${selectId}-error` : undefined}
                    className={cn(selectBase, error && 'ring-2 ring-brand-red/40 focus:ring-brand-red/30', className)}
                    {...props}
                >
                    {children}
                </select>

                <ChevronDown
                    aria-hidden
                    className='pointer-events-none absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-neutral-500'
                />
            </div>

            {error ? (
                <p id={`${selectId}-error`} className='text-xs font-medium text-brand-red'>
                    {error}
                </p>
            ) : null}
        </div>
    )
})

Select.displayName = 'Select'

export default Select
